import React from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { SelectElement, TextFieldElement, useForm } from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMCustomer } from "../graphql/queries";
import { GetLLMCustomerQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { updateLLMCustomerAfterEdit } from "../dataaccess/updateCustomerAfterEdit";
import { createLLMCustomerAfterEdit } from "../dataaccess/createLLMCustomerAfterEdit";
import { useUser } from "../contexts/useUser";

const schema = z.object({
  id: z.string().optional(),
  projectId: z.string().optional(),
  name: z.string().min(1, "Name is required").optional().nullable(),
  status: z.string().min(1, "Status is required"),
  tokensRemaining: z.coerce.number().int().nonnegative(),
  loadSheddingTier: z.number().int().min(1).max(4),
  dataResidency: z.string(),
  enterpriseUser: z.boolean().optional().nullable(),
  tokenReplenishmentAmount: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .nullable(),
  tokenReplenishmentPeriod: z.string().optional().nullable(),
});

type CustomerFormType = z.infer<typeof schema>;

const ProjectForm = () => {
  const { id } = useParams<{ id: string }>();
  const { project } = useUser();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();

  const queryClient = useQueryClient();
  const mutationUpdateCustomer = useMutation({
    mutationFn: updateLLMCustomerAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["customers", { id: id }] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating customer", error as any);
    },
  });

  const mutationCreateCustomer = useMutation({
    mutationFn: createLLMCustomerAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error creating customer", error as any);
    },
  });

  const {
    data: customerData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["customers", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMCustomer, { id: id })) as Promise<
        GraphQLResult<GetLLMCustomerQuery>
      >,
    enabled: !!id,
  });

  const { handleSubmit, control, reset } = useForm<CustomerFormType>({
    values: customerData?.data?.getLLMCustomer || {
      projectId: project?.id,
      name: "",
      status: "active",
      tokensRemaining: project?.newCustomerTokenCount || 0,
      loadSheddingTier: project?.newCustomerLoadSheddingTier || 2,
      dataResidency: "global",
    },
    resolver: zodResolver(schema),
  });

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading project</div>;

  const handleUpdate = (values: CustomerFormType) => {
    if (!values) return;

    try {
      mutationUpdateCustomer.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating customer", error as any);
    }
  };

  const handleCreate = (values: CustomerFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateCustomer.mutate({
      projectId: project?.id,
      ...values,
    });
  };
  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: CustomerFormType) => {
    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  return (
     <Paper elevation={3} style={{ borderRadius: 3, border: "1px solid #444", margin:2, padding:2 }}>
      <Typography padding={2} variant="h6">
        Customer
      </Typography>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="status"
            label="Customer status"
            size="small"
            required
            fullWidth
            options={[
              { id: "active", label: "Active" },
              { id: "disabled", label: "Disabled" },
              {
                id: "errors",
                label: "Disabled (due to errors)",
              },
              { id: "deleted", label: "Deleted" },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="tokensRemaining"
            label="Tokens Remaining"
            size="small"
            type="number"
            fullWidth
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="loadSheddingTier"
            label="Load Shedding Tier"
            size="small"
            required
            fullWidth
            options={[
              { id: 1, label: "Tier 1 (first to shed)" },
              {
                id: 2,
                label: "Tier 2 (customers shed after Tier 1)",
              },
              { id: 3, label: "Tier 3 customers (shed as last resort)" },
              { id: 4, label: "Never shed" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            name="dataResidency"
            label="Data Residency Restriction"
            control={control}
            required
            size="small"
            fullWidth
            options={[
              { id: "global", label: "Global - no restrictions" },
              { id: "americas", label: "North / Central / South America" },
              { id: "eu", label: "European Union (Member States of the EU)" },
              { id: "noneu", label: "Europe (Non-member states of the EU)" },
              { id: "apac", label: "Asia / Pacific / Oceania" },
              { id: "other", label: "Other" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="enterpriseUser"
            label="Allowed Enterprise User Access"
            size="small"
            fullWidth
            options={[
              { id: true, label: "Yes" },
              { id: false, label: "No" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="tokenReplenishmentAmount"
            label="Token Replenishment Amount"
            size="small"
            type="number"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="tokenReplenishmentPeriod"
            label="Token Replenishment Period"
            size="small"
            fullWidth
            options={[
              { id: "", label: "None" },
              { id: "monthly", label: "Beginning each month" },
              { id: "weekly", label: "Beginning each week" },
              { id: "daily", label: "Beginning each day" },
            ]}
          />
        </Grid>
      </Grid>
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default ProjectForm;
